/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  GWConnectorNode,
  GWNode,
} from '@/app/(dashboard)/saas-inventory/google-workspace/googleWorkspaceTypes';
import type { GWSnapshot } from '@/app/(dashboard)/saas-inventory/google-workspace/snapshotTypes';
import { googleWorkspaceMockData } from '@/app/api/mock/projects/[projectId]/saas-inventory/google-workspace/mockData';
import {
  snapshotRemovals,
  snapshotsMockData,
} from '@/app/api/mock/projects/[projectId]/saas-inventory/google-workspace/snapshots';

function filterTree<T extends { id: string; children?: T[] }>(
  nodes: T[],
  removeIds: Set<string>
): T[] {
  return nodes
    .filter((n) => !removeIds.has(n.id))
    .map((n) => ({
      ...n,
      children: n.children ? filterTree(n.children, removeIds) : undefined,
    }));
}

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function capDates(node: any, snapshotDate: string, sizeFactor: number): any {
  const snapTime = new Date(snapshotDate).getTime();
  const result = { ...node };
  if (result.modified) {
    const modTime = new Date(result.modified).getTime();
    if (modTime > snapTime) {
      const offsetMs = (1 + (simpleHash(result.id) % 24)) * 3_600_000;
      result.modified = new Date(snapTime - offsetMs).toISOString();
    }
  }
  if (result.date) {
    const dateTime = new Date(result.date).getTime();
    if (dateTime > snapTime) {
      const offsetMs = (1 + (simpleHash(result.id) % 48)) * 3_600_000;
      result.date = new Date(snapTime - offsetMs).toISOString();
    }
  }
  if (result.lastBackup) result.lastBackup = snapshotDate;
  if (result.size && sizeFactor < 1)
    result.size = Math.round(result.size * sizeFactor);
  if (result.children)
    result.children = result.children.map((child: any) =>
      capDates(child, snapshotDate, sizeFactor)
    );
  return result;
}

function buildConnectorTree(
  connectorId: string,
  snapshotId: string
): GWConnectorNode {
  const base =
    googleWorkspaceMockData.find((c) => c.id === connectorId) ??
    googleWorkspaceMockData[0];
  const latestId = snapshotsMockData[0]?.id;
  if (snapshotId === latestId) return base;

  const snapshot = snapshotsMockData.find((s) => s.id === snapshotId);
  if (!snapshot) return base;

  const sizeFactor = snapshot.sizeBytes / snapshotsMockData[0].sizeBytes;
  const removeIds = new Set(snapshotRemovals[snapshotId] ?? []);
  const filtered: GWNode[] = filterTree<GWNode>([base], removeIds).map((n) =>
    capDates(n, snapshot.date, sizeFactor)
  );
  const result = filtered[0] ?? base;
  return result.type === 'connector' ? result : base;
}

export const saasInventory = () => ({
  googleWorkspace: {
    useSnapshots: (): { data: GWSnapshot[]; isLoading: false } => ({
      data: snapshotsMockData,
      isLoading: false,
    }),
    useConnectorTree: (
      connectorId: string,
      snapshotId: string | null
    ): { data: GWConnectorNode | undefined; isLoading: false } => ({
      data: snapshotId
        ? buildConnectorTree(connectorId, snapshotId)
        : undefined,
      isLoading: false,
    }),
  },
});
